import React, { useState } from 'react';

// Live-meeting Q&A is transcript context, not training. We render every
// substantive Q+A exchange in two columns by speaker — seller-asked on the
// left, prospect-asked on the right — with no scoring, no coaching, no
// covered/missed lists. Practice-pitch Q&A keeps its full coaching shape
// (see ../PerformanceReviews/NewAssessmentDetails.jsx); changes here MUST
// NOT affect that flow.
//
// "Add to Q&A Library" lives ONLY on the prospect-asked column — those are
// objections / info-requests that fit the library's question/answer pair
// shape. Seller-asked questions are discovery prompts and don't belong in
// the same library. Mirrors the save-state + dedupe pattern from
// NewAssessmentDetails.jsx:160-247.

// Back-compat: items written before the schema split have
//   { question, userResponse, sourceQuote, keyPointsCovered, ... }
// items written after have
//   { askedBy, question, questionSpeaker, answer, answerSpeaker }
// Coalesce on read so old DDB rows still render — they all land in the
// prospect column with no speaker labels (the pre-split assumption).
const normalizeQa = (qa) => ({
    askedBy: qa.askedBy === 'seller' ? 'seller' : 'prospect',
    question: qa.question || '',
    questionSpeaker: qa.questionSpeaker || '',
    answer: qa.answer || qa.userResponse || '',
    answerSpeaker: qa.answerSpeaker || '',
});

const SaveButton = ({ qa, index, existingQuestions, onSaveToLibrary, savedState, setSavedState }) => {
    const questionText = (qa.question || '').trim();
    const alreadyInLibrary = existingQuestions.some(eq => (eq.question || '').trim() === questionText);
    const localState = savedState[index]; // 'saving' | 'saved' | undefined
    const isSaved = localState === 'saved' || alreadyInLibrary;
    const isSaving = localState === 'saving';

    if (isSaved) {
        return (
            <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: 600 }}>
                &#10003; In Q&amp;A Library
            </span>
        );
    }

    return (
        <button
            disabled={isSaving}
            onClick={async () => {
                setSavedState(prev => ({ ...prev, [index]: 'saving' }));
                try {
                    await onSaveToLibrary([{
                        question: qa.question,
                        answer: qa.answer || '',
                        related_to: 'Live Meeting',
                    }]);
                    setSavedState(prev => ({ ...prev, [index]: 'saved' }));
                } catch (err) {
                    console.error('Failed to save question to library:', err);
                    setSavedState(prev => ({ ...prev, [index]: undefined }));
                }
            }}
            style={{
                fontFamily: 'Montserrat',
                fontSize: '12px',
                fontWeight: 600,
                padding: '6px 14px',
                borderRadius: '4px',
                border: '1px solid #0784a7',
                backgroundColor: 'transparent',
                color: '#0784a7',
                cursor: isSaving ? 'default' : 'pointer',
            }}
        >
            {isSaving ? 'Adding…' : 'Add to Q&A Library'}
        </button>
    );
};

const Column = ({
    title,
    items,
    emptyLabel,
    showSaveButton,
    existingQuestions,
    onSaveToLibrary,
    savedState,
    setSavedState,
    keyPrefix,
}) => (
    <div style={{ flex: 1, minWidth: 0 }}>
        <h4 style={{
            color: '#1e293b',
            fontSize: '14px',
            fontWeight: 700,
            margin: '0 0 12px 0',
            paddingBottom: '8px',
            borderBottom: '2px solid #e2e8f0',
        }}>
            {title} <span style={{ color: '#94a3b8', fontWeight: 500 }}>({items.length})</span>
        </h4>
        {items.length === 0 ? (
            <div style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic', padding: '12px 0' }}>
                {emptyLabel}
            </div>
        ) : (
            items.map((qa, i) => (
                <div key={`${keyPrefix}_${i}`} style={{
                    padding: '14px',
                    marginBottom: '10px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', marginBottom: '6px', lineHeight: 1.4 }}>
                        Q{qa.questionSpeaker ? ` · ${qa.questionSpeaker}` : ''}: {qa.question}
                    </div>
                    <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
                        <strong style={{ color: '#0784a7' }}>A{qa.answerSpeaker ? ` · ${qa.answerSpeaker}` : ''}:</strong>{' '}
                        {qa.answer || <em style={{ color: '#94a3b8' }}>(no substantive response captured)</em>}
                    </div>
                    {showSaveButton && qa.question && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                            <SaveButton
                                qa={qa}
                                index={i}
                                existingQuestions={existingQuestions}
                                onSaveToLibrary={onSaveToLibrary}
                                savedState={savedState}
                                setSavedState={setSavedState}
                            />
                        </div>
                    )}
                </div>
            ))
        )}
    </div>
);

const LiveMeetingQABreakdown = ({
    assessment,
    canEditLibrary = false,
    existingQuestions = [],
    onSaveToLibrary,
}) => {
    const [savedState, setSavedState] = useState({});

    const raw = assessment?.qaAnalysis?.questionAnalysis;
    if (!Array.isArray(raw) || raw.length === 0) return null;

    const normalized = raw.map(normalizeQa);
    const sellerAsked = normalized.filter(q => q.askedBy === 'seller');
    const prospectAsked = normalized.filter(q => q.askedBy === 'prospect');

    const showSaveButton = canEditLibrary && typeof onSaveToLibrary === 'function';

    return (
        <div style={{ padding: '20px', fontFamily: 'Montserrat, sans-serif' }}>
            <h3 style={{ color: '#1e293b', fontSize: '16px', fontWeight: 700, marginBottom: '4px' }}>
                Question Breakdown
            </h3>
            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '16px' }}>
                Every substantive Q&amp;A exchange captured from the transcript.
            </div>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <Column
                    title="Questions you asked"
                    items={sellerAsked}
                    emptyLabel="No seller-asked questions detected."
                    showSaveButton={false}
                    keyPrefix="seller"
                />
                <Column
                    title="Questions they asked"
                    items={prospectAsked}
                    emptyLabel="No prospect-asked questions detected."
                    showSaveButton={showSaveButton}
                    existingQuestions={existingQuestions}
                    onSaveToLibrary={onSaveToLibrary}
                    savedState={savedState}
                    setSavedState={setSavedState}
                    keyPrefix="prospect"
                />
            </div>
        </div>
    );
};

export default LiveMeetingQABreakdown;
